/* eslint-disable import/no-named-as-default */
import React from 'react';
import moment from 'moment';

import Modal from '../../ui/Modal';
import MultipleChoiceSurvey, {
  SURVEY_QUESTION_ONE, SURVEY_QUESTION_TWO, SURVEY_QUESTION_THREE,
} from '../../shared/Surveys/MultipleChoiceSurvey';
import analytics from '../../../modules/analytics';
import { useMount } from '../../../hooks';
import StateManager from '../../../modules/user/stateManager';

export const MONTHLY_GOAL_SURVEY_KEY = 'proMonthlyGoalSurveyCompleted';

const MonthlyGoalSurvey = ({
  closeModal,
  linkTo,
  onSurveySubmitted,
  providerId,
  source,
}) => {
  const surveyTitle = 'Set a monthly goal and we’ll give you tips on how to get there.';
  const submitBtnText = 'Set goal';
  const responseForQuestionOne = SURVEY_QUESTION_ONE;
  const responseForQuestionTwo = SURVEY_QUESTION_TWO;
  const responseForQuestionThree = SURVEY_QUESTION_THREE;

  const questionOne = {
    question: 'I want to attract new clients',
    choices: [
      '2 new clients',
      '4 new clients',
      '6+ new clients',
    ],
  };

  const questionTwo = {
    question: 'I want to increase my monthly revenue',
    choices: [
      '$75 more',
      '$150 more',
      '$250+ more',
    ],
  };

  const questionThree = {
    question: 'I want to streamline my business',
    choices: [
      'Be an expert on StyleSeat features',
      'Transition my clients to StyleSeat',
      'Polish my service menu + pricing',
    ],
  };

  const questions = [questionOne, questionTwo, questionThree];

  useMount(() => {
    analytics.track('pro_goals_modal_viewed');
  });

  /**
   * handles tracking of selected choices from the goal survey
   * @param response the selected choices from the goal survey
   */
  const trackSelectedResponse = response => {
    let eventName;
    let trackingParams;

    if (responseForQuestionOne in response) {
      eventName = 'pro_goals_newclients_selected';
      switch (response[responseForQuestionOne]) {
        case 1:
          trackingParams = { choice: 2 };
          break;
        case 2:
          trackingParams = { choice: 4 };
          break;
        case 3:
          trackingParams = { choice: 6 };
          break;
        default:
          trackingParams = {};
      }
    }

    if (responseForQuestionTwo in response) {
      eventName = 'pro_goals_revenue_selected';
      switch (response[responseForQuestionTwo]) {
        case 1:
          trackingParams = { choice: 75 };
          break;
        case 2:
          trackingParams = { choice: 150 };
          break;
        case 3:
          trackingParams = { choice: 250 };
          break;
        default:
          trackingParams = {};
      }
    }

    if (responseForQuestionThree in response) {
      eventName = 'pro_goals_streamlinebusiness_selected';
      switch (response[responseForQuestionThree]) {
        case 1:
          trackingParams = { choice: 'SSFeatures' };
          break;
        case 2:
          trackingParams = { choice: 'transitionClients' };
          break;
        case 3:
          trackingParams = { choice: 'serviceAndPricing' };
          break;
        default:
          trackingParams = {};
      }
    }

    if (response.textResponse) {
      eventName = 'pro_goals_setmyown_selected';
      trackingParams = { choice: response.textResponse };
    }

    analytics.track(eventName, trackingParams);
  };

  /**
   * closes modal
   */
  const handleModalClose = () => {
    analytics.track('pro_goals_dismissed');
    closeModal();
  };

  const handleSubmit = async response => {
    const params = {
      time: moment.utc().format(),
      goal: response,
    };

    // Wait for this to be set so it is available on after the page transition
    await StateManager.addAction(MONTHLY_GOAL_SURVEY_KEY, params);

    // needed to remove the banner from the edit profile view
    onSurveySubmitted();
    trackSelectedResponse(response);
    analytics.track('pro_goals_save_clicked');
    closeModal();
    linkTo('provider.growth_tab.goals', { providerId, source });
  };

  return (
    <Modal
      appearance="fullScreen"
      theme="blue"
      showCloseInBody
      onCloseRequest={handleModalClose}
    >
      <MultipleChoiceSurvey
        name="monthlyGoalSurvey"
        title={surveyTitle}
        questions={questions}
        submitBtnText={submitBtnText}
        onSubmit={handleSubmit}
      />
    </Modal>
  );
};

export default MonthlyGoalSurvey;
